import { useState } from 'react';
import { Head, router } from '@inertiajs/react';
import MainLayout from '../../components/MainLayout';
import Container from '../../components/Container';
import ProductGrid from '../../components/ProductGrid';
import FilterControls from '../../components/FilterControls';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Filter } from 'lucide-react';
import Breadcrumb from '../../components/Breadcrumb';

import { ExclusiveProduct, StoreSettings, Product, Category, Brand } from "../../types";

interface Pagination {
  total: number;
  per_page: number;
  current_page: number;
  total_pages: number;
}

interface Filters {
  name?: string;
  category_id?: string;
  brand_id?: string;
  min_price?: string;
  max_price?: string;
}

interface ExclusiveProductsProps {
  products: Product[];
  categories: Category[];
  brands: Brand[];
  pagination: Pagination;
  filters: Filters;
  store_settings: StoreSettings;
  exclusive_products: ExclusiveProduct[];
}

const ExclusiveProducts = ({ products, categories, brands, pagination, filters, store_settings, exclusive_products }: ExclusiveProductsProps) => {
  const [nameFilter, setNameFilter] = useState(filters.name || '');
  const [categoryFilter, setCategoryFilter] = useState(filters.category_id || 'all');
  const [brandFilter, setBrandFilter] = useState(filters.brand_id || 'all');
  const maxPrice = 1000;
  const [priceRange, setPriceRange] = useState<[number, number]>([
    Number(filters.min_price || 0),
    Number(filters.max_price || maxPrice),
  ]);

  const applyFilters = () => {
    const params: any = {
      name: nameFilter,
      category_id: categoryFilter === 'all' ? '' : categoryFilter,
      brand_id: brandFilter === 'all' ? '' : brandFilter,
      min_price: priceRange[0],
      max_price: priceRange[1],
    };
    router.get('/productos-exclusivos', params, { preserveState: true, replace: true });
  };
  
  const resetFilters = () => {
    setNameFilter('');
    setCategoryFilter('all');
    setBrandFilter('all');
    setPriceRange([0, maxPrice]);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > pagination.total_pages) return;
    const params = { ...filters, page };
    router.get('/productos-exclusivos', params, { preserveState: true, replace: true });
  };

  const filterProps = {
    nameFilter,
    setNameFilter,
    categoryFilter,
    setCategoryFilter,
    brandFilter,
    setBrandFilter,
    priceRange,
    setPriceRange,
    maxPrice,
    categories,
    brands,
    applyFilters,
    resetFilters,
  };

  return (
    <MainLayout
      store_settings={store_settings}
      exclusiveProducts={exclusive_products}
    >
      <Head title="Productos Exclusivos" />
      <main className="flex-grow">
        <Container>
          <div className="py-12">
            <Breadcrumb items={[{ label: "Productos Exclusivos" }]} />
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold">Nuestros Productos Exclusivos</h1>
              <div className="md:hidden">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" size="icon">
                      <Filter className="h-4 w-4" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent>
                    <SheetHeader>
                      <SheetTitle>Filtros</SheetTitle>
                    </SheetHeader>
                    <div className="mt-4">
                      <FilterControls {...filterProps} />
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {/* Filters Sidebar for Desktop */}
              <aside className="hidden md:block md:col-span-1">
                <div className="sticky top-24">
                  <FilterControls {...filterProps} />
                </div>
              </aside>

              {/* Products Grid */}
              <div className="md:col-span-4">
                <ProductGrid products={products} />
                {/* Pagination */}
                {pagination.total_pages > 1 && (
                  <div className="mt-8 flex justify-center items-center space-x-2 md:space-x-4">
                    <Button
                      onClick={() => handlePageChange(pagination.current_page - 1)}
                      disabled={pagination.current_page <= 1}
                    >
                      Anterior
                    </Button>
                    <span className="text-sm md:text-base">
                      Página {pagination.current_page} de {pagination.total_pages}
                    </span>
                    <Button
                      onClick={() => handlePageChange(pagination.current_page + 1)}
                      disabled={pagination.current_page >= pagination.total_pages}
                    >
                      Siguiente
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Container>
      </main>
    </MainLayout>
  );
};

export default ExclusiveProducts;